// Splitting a section in two, as list surgery.
//
// "New section after this slide" is not a new kind of operation — it is the
// move the outline already performs, with a destination that does not exist
// yet. The slides that follow the selected slide leave their section together,
// in order, and land in a section created immediately after it.
//
// So this delegates the transfer to `planSlideMove` rather than splicing
// arrays of its own. That matters for one specific reason: a slide changing
// section has to be re-minted an id in the destination's namespace (slide ids
// encode their section, `<sectionId>-slide-N`), its blocks re-tagged with it,
// and the rename reported so the caller can carry that slide's name,
// background, subtitle and duration across. All of that already exists, under
// test, in slideMoves.js. A second implementation here would be a second place
// for it to be wrong.
//
// What is left is the part a move cannot express: deciding *which* slides go,
// where the new section sits in the deck, and what a section with nothing to
// receive should be left holding.
//
// Like slideMoves.js, this deliberately does not know how a section stores its
// slides — the caller splits and flattens — or what a slide id looks like. It
// is handed lists and hands lists back, which is what makes it testable
// without mounting the editor.

import { planSlideMove } from './slideMoves';

/**
 * The slides that would move, given a section's slides and the selected one.
 *
 * Exported because the dialog has to show this list *before* anything changes,
 * and a preview computed by different code from the operation it previews is a
 * preview that can lie. Both call this.
 *
 * Contiguous by construction: everything after the selected slide, to the end
 * of its own section, and nothing else. The next section's slides are never
 * candidates — they belong to a section the author did not ask about.
 *
 * @param {Array<object>} slides - one section's slides, in order
 * @param {object} at
 * @param {string} [at.slideId] - the selected slide, by id
 * @param {number} [at.slideIndex] - its position, used only when the id misses
 * @returns {{ index: number, selected: object|null, trailing: Array<object> }}
 *   `index` is -1 when the slide is not in this list, and `trailing` is then
 *   empty — a caller that cannot find the slide has nothing to move.
 */
export function trailingSlidesAfter(slides = [], { slideId = '', slideIndex = null } = {}) {
  const list = Array.isArray(slides) ? slides : [];
  let index = slideId ? list.findIndex((slide) => slide && slide.slideId === slideId) : -1;
  // Position is a fallback, never the first answer: it means nothing once
  // anything else in the deck has moved.
  if (index < 0 && Number.isFinite(slideIndex) && slideIndex >= 0 && slideIndex < list.length) {
    index = slideIndex;
  }
  if (index < 0) return { index: -1, selected: null, trailing: [] };
  return { index, selected: list[index], trailing: list.slice(index + 1) };
}

/**
 * Plan the creation of a new section immediately after the selected slide's
 * section, taking the rest of that section's slides with it.
 *
 * The selected slide always stays where it is. That is the whole contract, and
 * it is what makes the result predictable enough not to need a confirmation
 * step: the author points at a slide and says "the new section starts after
 * here".
 *
 * @param {object} options
 * @param {Record<string, Array<object>>} options.slidesBySection - every
 *   section's slides, in order, keyed by section id
 * @param {Array<string>} options.sectionOrder - section ids in deck order
 * @param {string} options.fromSectionId - the selected slide's section
 * @param {string} [options.afterSlideId] - the selected slide
 * @param {number} [options.afterSlideIndex] - fallback position
 * @param {string} options.newSectionId - already checked for collisions by the
 *   caller, which owns the deck's id namespace
 * @param {(sectionId: string) => string} options.mintSlideId
 * @param {(slideId: string) => Array<object>} [options.createBlankSlideBlocks] -
 *   the blocks a brand-new empty slide starts with. Injected rather than
 *   assumed: "what a new slide starts with" is a decision slideStarters.js owns
 *   (and it is deliberately an empty list), and this module should not quietly
 *   hold a second opinion about it.
 * @returns {object} `{ split: false, reason }` when nothing can be done, or the
 *   full plan. Only touched sections appear in `slidesBySection`.
 */
export function planSectionSplit({
  slidesBySection = {},
  sectionOrder = [],
  fromSectionId,
  afterSlideId = '',
  afterSlideIndex = null,
  newSectionId,
  mintSlideId = () => '',
  createBlankSlideBlocks = () => []
} = {}) {
  const source = slidesBySection || {};
  if (!fromSectionId || !Array.isArray(source[fromSectionId])) {
    return { split: false, reason: 'no-section' };
  }
  if (!newSectionId || source[newSectionId]) {
    return { split: false, reason: 'bad-section-id' };
  }

  const order = sectionOrder.length ? [...sectionOrder] : Object.keys(source);
  const fromIndex = order.indexOf(fromSectionId);
  if (fromIndex < 0) return { split: false, reason: 'no-section' };

  const { index, trailing } = trailingSlidesAfter(source[fromSectionId], {
    slideId: afterSlideId,
    slideIndex: afterSlideIndex
  });
  if (index < 0) return { split: false, reason: 'no-slide' };

  // Immediately after the section the selected slide is in — whether or not it
  // was that section's last slide, and whatever sits after it. A section that
  // already follows is pushed along, never rewritten.
  const insertSectionAtIndex = fromIndex + 1;

  if (!trailing.length) {
    // Nothing to receive. A section the rest of the app can represent has at
    // least one slide, so it gets one — blank, by the same definition "New
    // Blank Slide" uses, rather than seeded with text nobody asked for.
    const blankSlideId = mintSlideId(newSectionId);
    return {
      split: true,
      newSectionId,
      insertSectionAtIndex,
      slidesBySection: {
        [newSectionId]: [{ slideId: blankSlideId, blocks: createBlankSlideBlocks(blankSlideId) }]
      },
      // The source section is untouched: its slides did not move, so
      // re-flattening it would rewrite blocks for no reason.
      touchedSectionIds: [newSectionId],
      renamedIds: [],
      transferredSlideIds: [],
      transferredCount: 0,
      firstSlideId: blankSlideId,
      createdBlank: true
    };
  }

  // The destination has to exist for the move to land in it. It is empty, and
  // stays empty until the moved slides arrive — `planSlideMove` leaves an
  // emptied *source* holding a blank, but a destination that receives slides
  // never gets one.
  const withDestination = { ...source, [newSectionId]: [] };
  const orderWithDestination = [...order];
  orderWithDestination.splice(insertSectionAtIndex, 0, newSectionId);

  const plan = planSlideMove({
    slidesBySection: withDestination,
    sectionOrder: orderWithDestination,
    refs: trailing.map((slide, offset) => ({
      sectionId: fromSectionId,
      slideId: slide?.slideId,
      slideIndex: index + 1 + offset
    })),
    toSectionId: newSectionId,
    toIndex: 0,
    position: 'before',
    mintSlideId
  });

  if (!plan.moved) return { split: false, reason: 'move-failed' };

  return {
    split: true,
    newSectionId,
    insertSectionAtIndex,
    slidesBySection: plan.slidesBySection,
    touchedSectionIds: plan.touchedSectionIds,
    renamedIds: plan.renamedIds,
    transferredSlideIds: plan.movedSlideIds,
    transferredCount: plan.movedSlideIds.length,
    firstSlideId: plan.movedSlideIds[0] || '',
    createdBlank: false
  };
}

export default planSectionSplit;
